import React from "react";
import { Link } from "react-router-dom";
import useSEO from "../hooks/useSEO";
import Photo from "../components/Photo";
import Reveal from "../components/Reveal";
import { Arrow } from "../components/Icons";
import { PageHead, SectionHeading, Icon, Counter, CTABand } from "../components/Shared";
import { values } from "../data/content";
import { stats } from "../data/services";

export default function About() {
  useSEO({
    title: "About Victrie — Futuristic Software Company in Nairobi, Kenya",
    description: "Learn about Victrie, a Nairobi-based software development company on a mission to engineer next-generation AI, web, mobile and cloud solutions for Africa and the world. Meet our team and values.",
    path: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "About Victrie",
      url: "https://victrie.com/about",
      mainEntity: { "@id": "https://victrie.com/#organization" },
    },
  });

  return (
    <>
      <PageHead
        crumb="About"
        title={<>Engineering the <span className="grad-text">future</span>, from Nairobi to the world</>}
        text="Victrie was founded on a simple belief: the next generation of world-changing technology can, and will, be built in Africa."
      />

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container split">
          <Reveal>
            <Photo image="office-worker-2" alt="A Victrie engineer at work in the Nairobi office"
              hover
              style={{ aspectRatio: "5/4" }}
            />
          </Reveal>
          <Reveal delay={120}>
            <span className="eyebrow">Our story</span>
            <h2>Born to build what does not exist yet</h2>
            <p>Victrie started as a small group of engineers and designers who were tired of building the same software everyone else was building. We wanted to work on the hard problems: intelligent systems, products for hundreds of millions of mobile-first users, and infrastructure that could scale across a continent.</p>
            <p>Today we are a full-service software development company headquartered in Nairobi, Kenya, serving clients across Africa. Our work spans artificial intelligence, fintech, health, agriculture, logistics, education and enterprise software.</p>
            <p>What has not changed is our obsession: building futuristic, next-generation solutions with the craft and care of people who genuinely love technology.</p>
            <Link to="/solutions" className="btn btn--primary" style={{ marginTop: 8 }}>See upcoming solutions <Arrow /></Link>
          </Reveal>
        </div>
      </section>

      <section className="section--tight">
        <div className="container">
          <div className="stats">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 80} className="glass stat">
                <Counter value={s.value} suffix={s.suffix} />
                <span>{s.label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container split reverse">
          <Reveal delay={120}>
            <span className="eyebrow">Mission & vision</span>
            <h2>Our mission</h2>
            <p className="lead">To empower businesses everywhere with software that is intelligent, beautiful and built to last, so they can lead their industries into the future.</p>
            <h2 style={{ marginTop: 32 }}>Our vision</h2>
            <p className="lead">A world where the most advanced technology is engineered in Africa and used everywhere.</p>
          </Reveal>
          <Reveal>
            <Photo image="analyzing-data" alt="Engineer reflecting on a product decision"
              hover
              style={{ aspectRatio: "1/1" }}
            />
          </Reveal>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <SectionHeading eyebrow="Values" title="The principles behind every line of code" center />
          <div className="grid grid--3 values-grid">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 70} className="card">
                <div className="card__glow" />
                <div className="icon-badge"><Icon name={v.icon} /></div>
                <h3>{v.title}</h3>
                <p style={{ margin: 0 }}>{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <Reveal className="glass" style={{ padding: "clamp(32px,6vw,72px)", textAlign: "center" }}>
            <p className="big-quote" style={{ marginInline: "auto" }}>“We don't predict the future. We <span className="grad-text">build</span> it, one product at a time.”</p>
            <p style={{ color: "var(--muted)", marginTop: 20 }}>— The Victrie team</p>
          </Reveal>
        </div>
      </section>

      <CTABand title="Want to work with us, or for us?" text="We are always looking for exceptional clients and exceptional talent. Say hello at future@victrie.com." primary="Contact us" secondary="View services" />
    </>
  );
}
